import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, PlusIcon, ChevronLeftIcon, ChevronRightIcon, UsersIcon } from '@heroicons/react/24/outline';
import { Button, Input, Spinner, ModalPill } from '@components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui';
import {
  CARD,
  CTA,
  INSET_PANEL,
  MODAL_BACKDROP,
  MODAL_BODY,
  MODAL_CLOSE_X,
  MODAL_HEAD,
  MODAL_ICON_TILE,
  MODAL_KICKER,
  MODAL_PANEL,
  MODAL_TITLE,
} from '@components/ui/designTokens';
import { cn } from '@lib/utils/cn';
import { useCustomers } from '@features/customers/hooks/useCustomers';
import { displayCustomerName, displayCustomerTaxId, displayCustomerKind } from '@features/customers/utils/customerDisplay';
import { CustomerFormModal } from './CustomerFormModal';

interface CustomerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCustomer: (customer: any) => void;
}

export function CustomerSearchModal({ isOpen, onClose, onSelectCustomer }: CustomerSearchModalProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Solo buscar si hay 3 o más caracteres, o si está vacío (para mostrar todos)
  const searchQuery = debouncedSearch.length >= 3 || debouncedSearch.length === 0 ? debouncedSearch : '';

  // Detectar si es búsqueda por RUC o por nombre:
  // - Si contiene números → buscar por RUC (permite letras, guiones, espacios, etc.)
  // - Si solo tiene letras → buscar por nombre
  const hasNumbers = /\d/.test(searchQuery);

  const { data, isLoading } = useCustomers({
    page: currentPage,
    limit: 10,
    name: hasNumbers ? undefined : searchQuery,
    taxId: hasNumbers ? searchQuery : undefined,
  });

  // Debounce: esperar 1 segundo después de que el usuario deje de escribir
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);

    return () => clearTimeout(timer);
  }, [search]);

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  // El backend ya devuelve los datos paginados, usar directamente
  const limit = data?.limit || 10;
  const totalPages = data?.totalPages || 1;
  const paginatedCustomers = data?.customers || [];
  const startIndex = (currentPage - 1) * limit;
  const endIndex = startIndex + limit;

  // Debug: Log data and pagination state
  useEffect(() => {
    console.log('📊 CustomerSearchModal - Estado de paginación:', {
      currentPage,
      search,
      total: data?.total,
      totalPages,
      backendTotalPages: data?.totalPages,
      customersLength: data?.customers?.length,
      paginatedCustomersLength: paginatedCustomers.length,
    });
  }, [currentPage, search, data, totalPages, paginatedCustomers.length]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={MODAL_BACKDROP} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={cn('flex max-h-[calc(100vh-2rem)] max-w-4xl flex-col', MODAL_PANEL)}
              >
                {/* ---------- HEAD ---------- */}
                <div className={cn(MODAL_HEAD, 'shrink-0')}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3.5">
                      <span className={MODAL_ICON_TILE}>
                        <UsersIcon />
                      </span>
                      <div className="min-w-0">
                        <div className={MODAL_KICKER}>Facturación · Clientes</div>
                        <Dialog.Title as="h3" className={cn(MODAL_TITLE, 'mt-1')}>
                          Administrador de clientes
                        </Dialog.Title>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3.5">
                      {typeof data?.total === 'number' && (
                        <ModalPill tone="neutral">{data.total} en catálogo</ModalPill>
                      )}
                      <button
                        type="button"
                        onClick={onClose}
                        data-testid="invoice-wizard-customer-search-modal-close-button"
                        aria-label="Cerrar"
                        className={MODAL_CLOSE_X}
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* ---------- BODY ---------- */}
                <div className={cn(MODAL_BODY, 'min-h-0 flex-1 space-y-4')}>
                  {/* Search Bar */}
                  <div className={cn(INSET_PANEL, 'p-4')}>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <MagnifyingGlassIcon className="w-5 h-5 text-secondary-400" />
                          <Input
                            type="text"
                            placeholder="Buscar por nombre o RUC (mínimo 3 caracteres)..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="flex-1"
                           data-testid="invoice-wizard-customer-search-modal-search-input"/>
                          <button
                            type="button"
                            data-testid="invoice-wizard-customer-search-modal-new-customer-button"
                            onClick={() => setIsCreateModalOpen(true)}
                            className={cn(CTA, 'whitespace-nowrap')}
                          >
                            <PlusIcon className="w-4 h-4" />
                            Nuevo Cliente
                          </button>
                        </div>
                        {search.length > 0 && search.length < 3 && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 ml-8">
                            Escribe al menos 3 caracteres para buscar...
                          </p>
                        )}
                        {search !== debouncedSearch && search.length >= 3 && (
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 ml-8">
                            Buscando...
                          </p>
                        )}
                      </div>
                  </div>

                  {/* Results Table */}
                  <div className={cn(CARD, 'overflow-hidden')}>
                    <div>
                      {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                          <Spinner size="md" />
                        </div>
                      ) : paginatedCustomers.length === 0 ? (
                        <div className="text-center py-12">
                          <p className="text-sm text-secondary-500 dark:text-secondary-400">
                            No se encontraron clientes
                          </p>
                        </div>
                      ) : (
                        <>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nombre</TableHead>
                                <TableHead>RUC/ID</TableHead>
                                <TableHead>Tipo</TableHead>
                                <TableHead className="text-right">Acción</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedCustomers.map((customer) => (
                                <TableRow key={customer.id} data-testid={`invoice-wizard-customer-search-modal-result-${customer.id}`}>
                                  <TableCell className="font-medium">{displayCustomerName(customer)}</TableCell>
                                  <TableCell>
                                    {displayCustomerTaxId(customer)}{customer.dv ? `-${customer.dv}` : ''}
                                  </TableCell>
                                  <TableCell className="text-secondary-600 dark:text-secondary-400 text-xs">
                                    {displayCustomerKind(customer)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={() => onSelectCustomer(customer)}
                                      data-testid={`invoice-wizard-customer-search-modal-select-${customer.id}-button`}
                                    >
                                      Seleccionar
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {/* Pagination Controls */}
                          {data && totalPages > 1 && (
                            <div className="flex items-center justify-between border-t border-secondary-200 dark:border-secondary-700 px-6 py-4">
                              <div className="text-sm text-secondary-600 dark:text-secondary-400">
                                Mostrando {startIndex + 1} a {Math.min(endIndex, data.total)} de {data.total} clientes
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                  disabled={currentPage === 1}
                                  data-testid="invoice-wizard-customer-search-modal-prev-page-button"
                                  className="flex items-center gap-1"
                                >
                                  <ChevronLeftIcon className="w-4 h-4" />
                                  Anterior
                                </Button>
                                <div className="text-sm text-secondary-600 dark:text-secondary-400">
                                  Página {currentPage} de {totalPages}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                  disabled={currentPage === totalPages}
                                  data-testid="invoice-wizard-customer-search-modal-next-page-button"
                                  className="flex items-center gap-1"
                                >
                                  Siguiente
                                  <ChevronRightIcon className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>

        {/* Customer Form Modal */}
        <CustomerFormModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCustomerCreated={(customer) => {
            onSelectCustomer(customer);
            setIsCreateModalOpen(false);
          }}
        />
      </Dialog>
    </Transition>
  );
}
